import { useEffect, useMemo, useContext } from "react"

import { useCompareDataValues, useViewComparison } from "web/hooks/data"
import { LocalizationContext } from "web/contexts"

import NotEnoughItemsToCompare from "./NotEnoughItemsToCompare"
import HeaderColumn from "./HeaderColumn"
import DataColumn from "./DataColumn/DataColumn"
import ComparisonControls from "./ComparisonControls/ComparisonControls"

export default function CompareView({
  topic,
  data,
  view,
  rowsTitle,
  listType,
  onItemClick,
  setSelectedIds,
  subjectType,
}) {
  const { viewCurrency } = useContext(LocalizationContext)
  const { comparison } = view
  const {
    selected_subject_ids,
    show_averages,
    show_variances,
    show_variance_percentages,
    show_all,
  } = comparison

  const rows = useMemo(
    () =>
      view.fields.map((item) => ({
        ...item.field,
        presentation: item.presentation,
      })),
    [view.fields]
  )

  const currentSelection =
    JSON.parse(sessionStorage.getItem("selectedSubjectIdsToCompare")) || []

  const [comparisonData] = useViewComparison({
    view,
  })

  const {
    can_show_averages,
    can_show_variances,
    can_show_variance_percentages,
    averages,
    variances,
    variance_percentages,
  } = comparisonData

  const hasAverageAndMore =
    !!averages && (!!variances || !!variance_percentages)
  const hasAverages = can_show_averages && show_averages && !show_all
  const hasVariances = can_show_variances && show_variances && !show_all
  const hasVariancePercentages =
    can_show_variance_percentages && show_variance_percentages && !show_all

  const comparisonColumns = useMemo(() => {
    const columns = []
    if (hasAverages) columns.push("averages")
    if (hasVariances) columns.push("variances")
    if (hasVariancePercentages) columns.push("variance_percentages")
    return columns
  }, [hasAverages, hasVariances, hasVariancePercentages])

  useEffect(() => {
    setSelectedIds([])
  }, [])

  useEffect(() => {
    const newSelection = selected_subject_ids.filter((id) => {
      const isIdInCollection = data.find((datum) => datum.id === id)
      if (isIdInCollection) {
        return isIdInCollection.id
      } else {
        return false
      }
    })

    const isViewSelectionInSyncWithDataset =
      newSelection.length === selected_subject_ids.length

    if (currentSelection.length && isViewSelectionInSyncWithDataset) {
      currentSelection.length &&
        view.updateComparison({
          comparison_ids: currentSelection,
        })
    }

    if (newSelection && !isViewSelectionInSyncWithDataset) {
      view.updateComparison({
        comparison_ids: newSelection,
      })
    }
  }, [currentSelection, data])

  const { compareData } = useCompareDataValues({
    subjectType,
    topic,
    data,
    comparisonData,
    comparisonColumns,
    view,
    hasAverages,
    hasVariances,
    hasVariancePercentages,
    averages,
    variances,
    variance_percentages,
  })

  function isComparison(columnName) {
    return comparisonColumns.includes(columnName)
  }

  async function setUnselectedColumnAsReference(columnId) {
    await view.updateComparison({
      reference_subject_id: columnId,
      comparison_ids: [...selected_subject_ids, columnId],
    })
  }

  async function handleColumnSelectionChanged(value, columnData) {
    if (!value.value) {
      const updatedIdsList = selected_subject_ids.filter(
        (item) => item !== columnData.id
      )

      await view.updateComparison({
        comparison_ids: updatedIdsList.length ? updatedIdsList : null,
      })
    } else {
      await view.updateComparison({
        comparison_ids: [...selected_subject_ids, columnData.id],
      })
    }
  }

  async function onToggleReference(columnId, isComparison) {
    if (isComparison) return

    !selected_subject_ids.includes(columnId) &&
      setUnselectedColumnAsReference(columnId)

    if (comparison.reference_subject_id === columnId) {
      await view.updateComparison({
        reference_subject_id: null,
      })
    } else {
      await view.updateComparison({
        reference_subject_id: columnId,
      })
    }
  }

  if (data.length < 2) {
    return <NotEnoughItemsToCompare />
  }

  return (
    <div className="min-h-0 grow overflow-y-auto px-3 pb-24 md:px-5">
      <ComparisonControls
        show_all={show_all}
        comparisonData={comparisonData}
        subjectType={subjectType}
        listType={listType}
        view={view}
      />
      <div id="pivot-table-container" className="flex flex-auto overflow-auto">
        <HeaderColumn rowsTitle={rowsTitle} rows={rows} />
        <div className="flex items-start">
          {compareData.map((column, i) => {
            return (
              <DataColumn
                key={i}
                column={column}
                data={column}
                rows={rows}
                viewCurrency={viewCurrency}
                isComparison={isComparison(column.columnName)}
                hasAverageAndMore={hasAverageAndMore}
                selectedSubjectIds={selected_subject_ids}
                referenceSubjectId={comparison.reference_subject_id}
                onItemClick={onItemClick}
                onToggleReference={onToggleReference}
                handleColumnSelectionChanged={handleColumnSelectionChanged}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
